import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { api, type Me } from './api'
import type { Family, Space } from './families'
import { useMe } from './useMe'

// The space you're in, per family: the space switcher's choice. Switching filters the family's content to that
// room; it never changes the tree, which is the same map from every room. Remembered on this device; held in memory
// too, for browsers that refuse storage.
const chosen = new Map<string, string>()
const listeners = new Set<() => void>()
const key = (familyId: string) => `gatheredfrom:space:${familyId}`

function stored(familyId: string) {
  if (!chosen.has(familyId)) {
    try {
      const id = localStorage.getItem(key(familyId))
      if (id) chosen.set(familyId, id)
    } catch {
      // Private browsing can refuse storage; the default room is used.
    }
  }
  return chosen.get(familyId) ?? null
}

export function chooseSpace(familyId: string, spaceId: string) {
  chosen.set(familyId, spaceId)
  try {
    localStorage.setItem(key(familyId), spaceId)
  } catch {
    // Not remembered next time; it still switches.
  }
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// The current space in a family: the one you chose if you can still see it, otherwise the circle's own.
export function useCurrentSpace(family: Pick<Family, 'id' | 'spaces'> | null | undefined): Space | null {
  const id = useSyncExternalStore(subscribe, () => (family ? stored(family.id) : null))
  if (!family) return null
  return family.spaces.find((x) => x.id === id) ?? family.spaces.find((x) => x.kind === 'everyone') ?? null
}

// A space you can share a photo, recipe, moment, album or book with, in any of your circles. All your circles together,
// now and later, are "Everyone".
export type Place = Space & { familyId: string; circle: string }

// Who something is for: Everyone, or the places picked (neither: only whoever added it).
export type Audience = { everyone: boolean; spaceIds: string[] }
export const ONLY_ME: Audience = { everyone: false, spaceIds: [] }

// What the server says something is shared with, as a choice to start from.
export const audienceFrom = (x: { everyone?: boolean; sharedWith?: Place[] }): Audience =>
  x.everyone ? { everyone: true, spaceIds: [] } : { everyone: false, spaceIds: (x.sharedWith ?? []).map((y) => y.id) }

// A choice as a change sends it: Everyone, or the whole list of places (none for only whoever added it).
export const audienceBody = (x: Audience) =>
  x.everyone ? { everyone: true } : { everyone: false, spaceIds: x.spaceIds }

// The one place something is for, as a new album or book starts: the space you're looking from, else the circle's own.
// In your own space that's just you, and in Everyone, Everyone.
export const startingAudience = (
  family: Pick<Family, 'spaces' | 'personal' | 'everyone'>,
  space?: Space | null,
): Audience => {
  if (family.everyone) return { everyone: true, spaceIds: [] }
  if (family.personal) return ONLY_ME
  const id = space?.id ?? family.spaces.find((x) => x.kind === 'everyone')?.id
  return id ? { everyone: false, spaceIds: [id] } : ONLY_ME
}

export const useSharePlaces = () =>
  useQuery({
    queryKey: ['share-places'],
    queryFn: async () => (await api<{ places: Place[] }>('/share-places')).places,
  })

// Who something is for, in a few words: "Everyone", the places it's shared with, or `none` while it's only its author's.
export const audienceSaid = (x: { everyone?: boolean; sharedWith?: Place[] }, none: string, named?: boolean) => {
  if (x.everyone) return 'Everyone'
  const places = x.sharedWith ?? []
  return places.length ? placesSaid(places, named ?? new Set(places.map((y) => y.familyId)).size > 1) : none
}

// What a list of places says, in a few words: a circle's name when it's shared with everyone in it, "The Johnsons",
// or with their circles named: "The Johnsons in Taras Family and Supper Club".
export function placesSaid(places: Place[], named: boolean) {
  // Who in each circle: the whole circle by its name (which covers its homes), or its smaller spaces.
  const byCircle = new Map<string, Place[]>()
  for (const x of places) byCircle.set(x.circle, [...(byCircle.get(x.circle) ?? []), x])
  return listed(
    [...byCircle].map(([circle, here]) => {
      if (here.some((x) => x.kind === 'everyone')) return circle
      const who = listed([...new Set(here.map((x) => x.name))])
      return named ? `${who} in ${circle}` : who
    }),
  )
}

// "A", "A and B", "A, B and C".
const listed = (words: string[]) =>
  words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words.at(-1)}` : (words[0] ?? '')

// A new thing starts with whoever they shared their last new thing with, moments included (Dallas, 2026-10-17). Before
// their first, it's Everyone once that's offered (Dallas, 2026-10-17), and else `fallback`. Saved for the person, so it follows them to every device and every circle; changing who sees
// something already shared never changes it.
export function useLastShare() {
  const queryClient = useQueryClient()
  const me = useMe().data
  const last = me?.lastShare
  return {
    start: (fallback: Audience): Audience => last ?? (me?.everyone ? { everyone: true, spaceIds: [] } : fallback),
    remember: (who: Audience) => {
      const lastShare = { everyone: who.everyone, spaceIds: who.everyone ? [] : who.spaceIds }
      queryClient.setQueryData<Me | null>(['me'], (me) => (me ? { ...me, lastShare } : me))
      // Only a starting point for next time: if it doesn't save, nothing is lost.
      api('/me/last-share', { method: 'PUT', body: lastShare }).catch(() => {})
    },
  }
}
